import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';


import { AnalyticsService } from '@core/analytics.service';
import { AppConfigService } from '@core/appconfig.service';
import { AuthenticationService } from '@core/authentication.service';
import { CacheService } from '@core/cache.service';
import { UserService } from '@core/user.service';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { MobileBrowserTypes } from '@shared/enums/mobile-browser-types';
import { QrCodeHelpModalComponent } from '@shared/modals/qr-code-help-modal/qr-code-help-modal.component';
import { Html5Qrcode } from 'html5-qrcode';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';

// @todo remove this when SKOF app goes away.
// NOTE: apparently this is used by the App Stores and needs to stay for now
@Component({
  selector: 'zbp-login-skof',
  templateUrl: './login.skof.component.html',
  styleUrls: ['./login.skof.component.scss'],
  standalone: false
})
export class SkofLoginComponent implements OnInit, OnDestroy {
  form: FormGroup = null;
  error = null;
  showPasswordResetMessage = false;
  isAndroidUser = false;
  isScanningQRCode = false;
  loginProcessing = false;
  html5QrCode: any;
  userServiceSubscription: Subscription;
  helpQRCodeModalRef: NgbModalRef;

  constructor(
    private router: Router,
    private authService: AuthenticationService,
    private appConfigService: AppConfigService,
    private titleService: Title,
    private cache: CacheService,
    private userService: UserService,
    private toastr: ToastrService,
    private analyticsService: AnalyticsService,
    private modalService: NgbModal,
  ) {}

  onSubmit() {
    if (this.form.valid) {
      this.loginProcessing = true;
      this.error = null;
      const { password, schoolId } = this.form.value;

      this.authService.login(this.buildUserString(), password)
        .subscribe((res) => {
          if (res.success) {
            this.cache.lastSchoolIdUsed = schoolId;
            this.cache.setSkofAsLastLoginPathUsed();
            this.analyticsService.setUserProperties();
            this.router.navigateByUrl('');
          } else {
            const [error] = res.messages;
            this.error = error;
            this.loginProcessing = false;
          }
        }, () => {
          this.loginProcessing = false;
        });
    } else {
      this.error = 'Username, school id, and password cannot be blank';
      this.loginProcessing = false;
    }
  }

  ngOnInit() {
    const schoolId = this.cache.lastSchoolIdUsed || null;

    this.appConfigService.inSkofApp = !!window.zbLoginInterop || false;
    this.form = new FormGroup({
      username: new FormControl(null, [Validators.required]),
      password: new FormControl(null, [Validators.required]),
      schoolId: new FormControl(schoolId, [Validators.required]),
    });

    this.titleService.setTitle(`Student Login | ${this.appConfigService.getBrandTitle}`);

    const unityBrowser = localStorage.getItem('myBrowser');
    if (unityBrowser && unityBrowser === MobileBrowserTypes.UNITY) {
      const isAndroid = localStorage.getItem('isAndroid');
      if (isAndroid && isAndroid === 'true') {
        this.isAndroidUser = true;
      }
    }
  }

  buildUserString(): string {
    const { username, schoolId } = this.form.value;
    return `${username}_${schoolId}`;
  }


  prepQRCodeScan() {
    this.isScanningQRCode = true;
    Html5Qrcode.getCameras().then((devices) => {
      if (devices && devices.length) {
        this.scanQRCode();
      }
    }).catch((err) => {
      console.error(err);
      this.toastr.error('Please ask for help to use the camera');
      this.isScanningQRCode = false;
    });
  }

  scanQRCode() {
    const config = { fps: 10, qrbox: { width: 200, height: 200 } };
    this.html5QrCode = new Html5Qrcode('qrCodeScanner');
    this.html5QrCode.start({ facingMode: 'environment' }, config, (decodedText) => {
      this.parseAndLogin(decodedText);
      this.cancelQRCodeScan();
    }, (errorMessage) => {
      /* ignore insignificant media format chatter */
      if (errorMessage) {}
    })
      .catch((err) => {
        console.error(err);
        this.toastr.error(err);
      });
  }

  parseAndLogin(decodedText: string) {
    let studentIdPosition = decodedText.indexOf('=');
    studentIdPosition++;
    let tokenPosition = decodedText.indexOf('&');
    const studentId = decodedText.substring(studentIdPosition, tokenPosition);
    tokenPosition += 7; // advance past parameter key
    const token = decodedText.substring(tokenPosition, decodedText.length);

    this.userServiceSubscription = this.userService.loginUserByQRCode(studentId, token).subscribe((res) => {
      if (res.success) {
        this.cache.coreApiToken = res.response.token;
        this.userService.setUserData(res.response);
        this.authService.authStatus.next(true);
        this.cache.setStudentAsLastLoginPathUsed();
        this.analyticsService.setUserProperties();
        this.router.navigateByUrl('');
      } else {
        let error;
        if (res.messages) {
          error = res.messages;
        } else {
          error = 'An error has occurred. Please try again later.';
        }
        this.error = error;
        this.toastr.error(error);
      }
    });
  }

  cancelQRCodeScan() {
    this.html5QrCode.stop().then((ignore) => {
      if (ignore) {}
      this.html5QrCode.clear();
      this.isScanningQRCode = false;
    }).catch((err) => {
      console.error(err);
      this.toastr.error(err);
    });
  }

  getQRCodeHelp() {
    this.helpQRCodeModalRef = this.modalService.open(QrCodeHelpModalComponent, { size: 'lg' });
  }

  ngOnDestroy(): void {
    this.userServiceSubscription?.unsubscribe();
  }

}
